import axios, { AxiosError } from 'axios';
import { FileUploadSetting, FormAuthenticatedData } from 'backend';
import { ERROR_RESPONSE_STATUS, getUploadedFileInfoFromPiclib } from 'fetcher';
import { getThumbnailTypeFromPiclibFileUploadMediaFormat } from 'models';
import {
  FileUploadThumbnail,
  FILE_UPLOAD_THUMBNAIL_TYPE,
  FormPageInputItemFileUpload,
  FormPageProperty,
  mappedConst,
} from 'shared';

const getDataUrl = (imageBlob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(imageBlob);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (_e) => {
      reject();
    };
  });

const getThumbnailDataUrl = async (linkUrl: string) => {
  try {
    const response = await axios.get<Blob>(linkUrl, {
      responseType: 'blob',
    });
    const dataUrl = await getDataUrl(response.data);
    return dataUrl;
  } catch (error) {
    return '';
  }
};

type RequestUploadedFileResultType = 'ok' | 'notFound' | 'needsToReload';
export const REQUEST_UPLOADED_FILE_RESULT_TYPE: mappedConst<RequestUploadedFileResultType> =
  {
    ok: 'ok',
    notFound: 'notFound',
    needsToReload: 'needsToReload',
  };

type RequestUploadedFileResultOk = {
  type: Extract<RequestUploadedFileResultType, 'ok'>;
  thumbnail: FileUploadThumbnail;
};

type RequestUploadedFileResultNotFound = {
  type: Extract<RequestUploadedFileResultType, 'notFound'>;
};

type RequestUploadedFileResultNeedsToReload = {
  type: Extract<RequestUploadedFileResultType, 'needsToReload'>;
};

type Result =
  | RequestUploadedFileResultOk
  | RequestUploadedFileResultNotFound
  | RequestUploadedFileResultNeedsToReload;

type Payload = {
  fileSrn: NonNullable<FormPageInputItemFileUpload['fileSrn']>;
  propertyId: FormPageProperty['propertyId'];
  thumbnailApiEndpoint: FileUploadSetting['thumbnailApiEndpoint'];
  authentication: FormAuthenticatedData | undefined;
};
export const requestUploadedFile = async ({
  fileSrn,
  propertyId,
  thumbnailApiEndpoint,
  authentication,
}: Payload): Promise<Result> => {
  try {
    if (!thumbnailApiEndpoint) {
      throw new Error('Thumbnail api endpoint is not exist!');
    }
    if (authentication == null) {
      throw new Error('Authentication is not exist!');
    }
    const { linkUrl, mediaFormat, originalName } =
      await getUploadedFileInfoFromPiclib({
        fileSrn,
        propertyId,
        thumbnailApiEndpoint,
        authentication,
      });

    const thumbnailType =
      getThumbnailTypeFromPiclibFileUploadMediaFormat(mediaFormat);
    if (thumbnailType !== FILE_UPLOAD_THUMBNAIL_TYPE.image) {
      return {
        type: REQUEST_UPLOADED_FILE_RESULT_TYPE.ok,
        thumbnail: {
          type: thumbnailType,
          name: originalName,
          url: '',
        },
      };
    }
    const url = await getThumbnailDataUrl(linkUrl);

    return {
      type: REQUEST_UPLOADED_FILE_RESULT_TYPE.ok,
      thumbnail: {
        type: thumbnailType,
        name: originalName,
        url,
      },
    };
  } catch (e) {
    const error = e as AxiosError;
    if (error.response?.status === ERROR_RESPONSE_STATUS.notFound) {
      return {
        type: REQUEST_UPLOADED_FILE_RESULT_TYPE.notFound,
      };
    }
    return {
      type: REQUEST_UPLOADED_FILE_RESULT_TYPE.needsToReload,
    };
  }
};
